/**
 * ``GlyphIcon`` — a tiny inline-SVG rendering of a marker {@link GlyphId},
 * the DOM counterpart to the SDF the map paints (REQ-UI-086/087/088). Used by
 * the Profile editor's glyph picker swatches (and available to any future
 * legend) so a picker swatch is a faithful, crisp preview of the painted
 * marker shape.
 *
 * The path geometry comes from {@link glyphSvgPathData} — the *same* unit-space
 * outline {@link drawGlyphPath} traces onto the SDF canvas — so the swatch and
 * the map marker never drift (CLAUDE.md §4.4). Colour is ``currentColor`` so a
 * swatch tints to whatever the surrounding control sets (the picker paints the
 * selected swatch the action accent, the rest muted), matching how the map's
 * ``icon-color`` tints the SDF.
 *
 * Inline SVG (no external asset) keeps the air-gap posture (CLAUDE.md §8.2 /
 * REQ-UI-035 — no CDN icon fetch), consistent with the map's inline-SVG reset
 * control.
 *
 * Cross-references:
 *   - REQ-UI-086/087/088 (marker glyphs) / REQ-UI-094 (labelled control).
 *   - map/glyphs.ts (the shared geometry source).
 */
import { glyphSvgPathData, normalizeGlyphId, GLYPH_SVG_VIEWBOX, type GlyphId } from "@/map/glyphs";

/** Props for {@link GlyphIcon}. */
export interface GlyphIconProps {
  /** The glyph to render. An unknown id is normalised to the default circle
   *  (forward-compat / hand-edited Profile) rather than rendering nothing. */
  glyph: GlyphId | string;
  /** Square size in px (default 16 — the picker swatch size). */
  size?: number;
  /** Extra classes (e.g. the picker's selected/idle colour). */
  className?: string;
}

/**
 * Render a marker glyph as an inline SVG filled with ``currentColor``.
 *
 * ``aria-hidden`` — the icon is decorative; its accessible name is carried by
 * the labelled control that wraps it (the picker's swatch ``aria-label`` /
 * the ``GLYPH_LABELS`` text), per REQ-UI-094.
 */
export function GlyphIcon({ glyph, size = 16, className }: GlyphIconProps): React.ReactElement {
  const resolved = normalizeGlyphId(glyph);
  return (
    <svg
      width={size}
      height={size}
      viewBox={GLYPH_SVG_VIEWBOX}
      className={className}
      aria-hidden="true"
      focusable="false"
      data-glyph={resolved}
    >
      <path d={glyphSvgPathData(resolved)} fill="currentColor" />
    </svg>
  );
}
